import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import {
  fetchPolicies,
  fetchClaims,
  createClaim,
  openClaim,
  sendMessage,
  uploadEvidence,
  submitClaim,
  setCurrentChat,
  setCurrentChatToGpt,
  setSelectedPolicyId,
  setClaimId,
  toggleSidebar,
  updateShowSubmitClaim,
  addLocalUserMessage,
  checkForFreeCare,
} from '../store/slices/chatSlice';
import { ChatMessage } from '@/types';

export const useChat = () => {
  const dispatch = useDispatch<AppDispatch>();
  const chatState = useSelector((state: RootState) => state.chat);
  const [message, setMessage] = useState('');

  const handleFetchPolicies = () => {
    dispatch(fetchPolicies());
  };

  const handleFetchClaims = () => {
    dispatch(fetchClaims());
  };

  const handleCreateClaim = async (args: { policyId: string }) => {
    try {
      const createdClaim = await dispatch(createClaim(args)).unwrap();
      const record =
        createdClaim && typeof createdClaim === "object" ? createdClaim : {};
      const id = record.id || record.claim_id || record.claimId || record.claim?.id;
      return { ...record, id };
    } catch (error) {
      console.error('Failed to create claim:', error);
      throw error;
    }
  };

  const handleOpenClaim = async (args: { policyId: string; claimId?: string }) => {
    try {
      const opened = await dispatch(openClaim(args)).unwrap();
      const claim =
        opened && typeof opened === "object" ? opened : ({} as any);
      const id = claim.id ?? claim.claim_id ?? claim.claim?.id ?? args.claimId;
      return { ...claim, id };
    } catch (error) {
      console.error('Failed to open claim:', error);
      throw error;
    }
  };

  // ✅ New handler for Free Care
  const handleCheckForFreeCare = async (args: { policyId: string }) => {
    try {
      // Dispatch the thunk and unwrap the result (backend response)
      const result = await dispatch(checkForFreeCare(args)).unwrap();
      // The reducer already sets the state.claimId, but we return the raw result
      // so the component can navigate using result.id immediately.
      return result;
    } catch (error) {
      console.error('Failed to check for free care:', error);
      throw error;
    }
  };
  

  // const handleSendMessage = async (message: ChatMessage) => {
  //   if (!chatState.claimId) return;

  //   dispatch(setCurrentChat([...chatState.currentChat, message]));
  //   dispatch(setCurrentChatToGpt([...chatState.currentChatToGpt, message]));
  //   setMessage('');

  //   await dispatch(
  //     sendMessage({
  //       message,
  //       claimId: chatState.claimId,
  //       currentChatToGpt: chatState.currentChatToGpt,
  //     })
  //   );

  //   // No longer needed here - handled automatically in the slice
  //   // dispatch(updateShowSubmitClaim());
  // };

  const handleSendMessage = async (message: ChatMessage) => {
    if (!chatState.claimId) return;

    // 1. SAFELY append message to UI (fixes the overwrite bug)
    dispatch(addLocalUserMessage(message));
    setMessage('');

    // 2. Dispatch API call (thunk fetches fresh state internally)
    await dispatch(
      sendMessage({
        message,
        claimId: chatState.claimId,
      })
    );
  };

  const handleUploadEvidence = async (formData: FormData) => {
    if (!chatState.claimId) return;
    return await dispatch(uploadEvidence({ formData, claimId: chatState.claimId })).unwrap();
  };

  const handleSubmitClaim = async () => {
    if (!chatState.claimId) return;
    await dispatch(submitClaim(chatState.claimId)).unwrap();
  };

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
  };

  const handleSetSelectedPolicy = (policyId: string) => {
    dispatch(setSelectedPolicyId(policyId));
  };

  const handleSetClaimId = (claimId: string) => {
    dispatch(setClaimId(claimId));
  };

  return {
    ...chatState,
    message,
    setMessage,
    handleFetchPolicies,
    handleFetchClaims,
    handleCreateClaim,
    handleOpenClaim,
    handleCheckForFreeCare,
    handleSendMessage,
    handleUploadEvidence,
    handleSubmitClaim,
    handleToggleSidebar,
    handleSetSelectedPolicy,
    handleSetClaimId,
  };
};
